import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPictureMedium, setPictureMedium } from "../api";
import { useT } from "../i18n";

/**
 * PictureMedium is the one medium a book's pictures are drawn in (ADR-0168,
 * requirement 0098): a few words — "ink and watercolour, in the manner of
 * Victorian children's book illustration" — told to every scene and cover.
 *
 * It exists because one book's 48 pictures came out in three media: each
 * chapter's first picture chose its own. Empty means nobody has chosen yet, and
 * the first picture will; after that only the author changes it here.
 *
 * A setting, and free: it draws nothing and changes no picture that exists, so
 * it saves when the field is left and has no button — and it is never part of a
 * press that spends (ADR-0096). A text field, not a list to pick from: the
 * words are the author's or the model's, and a medium nobody listed is the
 * usual one.
 */
export default function PictureMedium({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const key = ["picture-medium", bookID];
  const medium = useQuery({ queryKey: key, queryFn: () => getPictureMedium(bookID) });
  const save = useMutation({
    mutationFn: (words: string) => setPictureMedium(bookID, words),
    onSuccess: (saved) => client.setQueryData(key, saved),
  });

  if (!medium.data) return null;
  return (
    <div data-testid="picture-medium">
      <p className="block text-sm font-medium">{t.book.pictureMedium}</p>
      <p className="mt-1 mb-2 text-xs text-muted">{t.book.pictureMediumNote}</p>
      <Field
        // Starts again from the saved words whenever the server's change.
        key={medium.data.medium}
        value={medium.data.medium}
        max={medium.data.max_chars}
        disabled={save.isPending}
        onChange={(words) => save.mutate(words)}
      />
      {save.isError && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t.book.pictureMediumFailed}
        </p>
      )}
    </div>
  );
}

function Field({
  value,
  max,
  disabled,
  onChange,
}: {
  value: string;
  max: number;
  disabled: boolean;
  onChange: (words: string) => void;
}) {
  const t = useT();
  // Shown as typed and committed on blur, as the narration direction is: bound
  // to the server it would save a character at a time.
  const [draft, setDraft] = useState(value);
  // Characters, not UTF-16 units or bytes: the server counts runes.
  const over = [...draft.trim()].length > max;
  const commit = (next: string) => {
    const trimmed = next.trim();
    if (trimmed !== value && [...trimmed].length <= max) onChange(trimmed);
  };

  return (
    <>
      {/* dir="auto" on the input holding the authored words and nowhere
          around it: a Persian medium lays out right to left. */}
      <input
        type="text"
        dir="auto"
        data-testid="picture-medium-input"
        aria-label={t.book.pictureMedium}
        value={draft}
        disabled={disabled}
        placeholder={t.book.pictureMediumPlaceholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="min-h-11 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm disabled:opacity-50"
      />
      {over && (
        <p role="alert" className="mt-1 text-xs text-red-600" data-testid="picture-medium-too-long">
          {t.book.pictureMediumTooLong(max)}
        </p>
      )}
      {value !== "" && (
        <button
          type="button"
          data-testid="picture-medium-clear"
          disabled={disabled}
          onClick={() => {
            setDraft("");
            onChange("");
          }}
          className="mt-2 rounded-full border border-line px-3 py-1 text-xs text-muted transition-colors hover:bg-sunk hover:text-ink disabled:opacity-50"
        >
          {t.book.pictureMediumClear}
        </button>
      )}
    </>
  );
}
